// src/components/Common/ClaimStudyModal.jsx
import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  IconButton,
  Chip,
  Alert,
  Divider,
  CircularProgress,
  Stack,
} from '@mui/material';
import {
  Lock as LockIcon,
  LockOpen as LockOpenIcon,
  Close as CloseIcon,
  Visibility as ViewIcon,
  Assignment as ReportIcon,
  PriorityHigh as StatIcon,
  Person as PersonIcon,
  Schedule as ScheduleIcon,
  LocalHospital as HospitalIcon,
} from '@mui/icons-material';

export default function ClaimStudyModal({
  open,
  onClose,
  study,
  currentUserId,
  onConfirmClaim,
  onUnclaim,
  onViewOnly,
  isDark = true,
}) {
  const [claiming, setClaiming] = useState(false);
  const [unclaiming, setUnclaiming] = useState(false);
  const [error, setError] = useState('');

  if (!study) return null;

  const assignedId = (study.assignedRadiologist?._id || study.assignedRadiologist)?.toString();
  const currentUserIdStr = currentUserId?.toString();

  const isClaimedByMe = Boolean(assignedId && currentUserIdStr && assignedId === currentUserIdStr);
  const isClaimedByOther = Boolean(
    assignedId &&
    currentUserIdStr &&
    assignedId !== currentUserIdStr &&
    ['claimed', 'in_progress'].includes(study.dycovueReportStatus)
  );

  const assignedName =
    study.assignedRadiologist?.firstName
      ? `Dr. ${study.assignedRadiologist.firstName} ${study.assignedRadiologist.lastName || ''}`
      : study.assignedRadiologistName
      ? `Dr. ${study.assignedRadiologistName}`
      : 'Another Radiologist';

  const handleClaim = async () => {
    setError('');
    setClaiming(true);
    try {
      if (onConfirmClaim) {
        await onConfirmClaim(study);
      }
      onClose();
    } catch (err) {
      console.error('Claim modal error:', err);
      setError(err.response?.data?.error || err.message || 'Failed to claim study');
    } finally {
      setClaiming(false);
    }
  };

  const handleUnclaim = async () => {
    setError('');
    setUnclaiming(true);
    try {
      if (onUnclaim) {
        await onUnclaim(study);
      }
      onClose();
    } catch (err) {
      console.error('Unclaim modal error:', err);
      setError(err.response?.data?.error || err.message || 'Failed to unclaim study');
    } finally {
      setUnclaiming(false);
    }
  };

  const handleView = () => {
    if (onViewOnly) {
      onViewOnly(study);
    }
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={claiming ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: isDark ? '#1E1E1E' : '#FFFFFF',
          color: isDark ? '#F1F5F9' : '#0F172A',
          borderRadius: '10px',
          border: `1px solid ${isDark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.1)'}`,
          boxShadow: isDark ? '0 12px 36px rgba(0,0,0,0.7)' : '0 12px 36px rgba(0,0,0,0.12)',
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          py: 1.5,
          px: 2.5,
          borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'}`,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {isClaimedByOther ? (
            <LockIcon sx={{ color: '#EF4444', fontSize: 22 }} />
          ) : (
            <LockOpenIcon sx={{ color: '#6366F1', fontSize: 22 }} />
          )}
          <Typography variant="h6" sx={{ fontSize: '0.95rem', fontWeight: 700 }}>
            {isClaimedByOther
              ? 'Study Locked by Radiologist'
              : isClaimedByMe
              ? 'Study Already Claimed by You'
              : 'Claim Study for Reporting'}
          </Typography>
        </Box>
        <IconButton size="small" onClick={onClose} disabled={claiming} sx={{ color: isDark ? '#94A3B8' : '#64748B' }}>
          <CloseIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 2.5 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2, fontSize: '0.78rem' }}>
            {error}
          </Alert>
        )}

        {isClaimedByOther ? (
          <Alert
            severity="warning"
            icon={<LockIcon fontSize="small" />}
            sx={{
              mb: 2,
              fontSize: '0.8rem',
              bgcolor: isDark ? 'rgba(245,158,11,0.1)' : '#FEF3C7',
              color: isDark ? '#FCD34D' : '#92400E',
            }}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: '0.82rem' }}>
              Locked by {assignedName}
            </Typography>
            This study was claimed for reporting. It is currently locked to prevent duplicate or conflicting reports. You can still open and review the DICOM images in read-only mode.
          </Alert>
        ) : (
          <Typography variant="body2" sx={{ mb: 2, color: isDark ? '#CBD5E1' : '#475569', fontSize: '0.82rem' }}>
            Would you like to <strong>claim and lock</strong> this study? Claiming assigns the study exclusively to your queue and prevents other radiologists from working on it simultaneously.
          </Typography>
        )}

        {/* Study Details Card */}
        <Box
          sx={{
            p: 1.75,
            borderRadius: '8px',
            bgcolor: isDark ? '#141414' : '#F8FAFC',
            border: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : '#E2E8F0'}`,
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.25 }}>
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: '0.88rem', color: isDark ? '#F8FAFC' : '#0F172A' }}>
                {study.patientName || study.patientId || 'Patient Record'}
              </Typography>
              <Typography variant="caption" sx={{ color: isDark ? '#94A3B8' : '#64748B' }}>
                ID: {study.patientId || '—'} {study.accessionNumber ? `• Acc: ${study.accessionNumber}` : ''}
              </Typography>
            </Box>

            <Stack direction="row" spacing={0.75}>
              {study.modality && (
                <Chip
                  label={study.modality}
                  size="small"
                  color="primary"
                  sx={{ height: 20, fontSize: '0.68rem', fontWeight: 700 }}
                />
              )}
              {study.dycovuePriority === 'stat' ? (
                <Chip
                  label="STAT"
                  color="error"
                  size="small"
                  icon={<StatIcon sx={{ fontSize: 13 }} />}
                  sx={{ height: 20, fontSize: '0.68rem', fontWeight: 700 }}
                />
              ) : (
                <Chip
                  label="Routine"
                  size="small"
                  variant="outlined"
                  sx={{ height: 20, fontSize: '0.68rem' }}
                />
              )}
            </Stack>
          </Box>

          <Divider sx={{ my: 1, borderColor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)' }} />

          <Stack spacing={0.6}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="caption" sx={{ color: isDark ? '#94A3B8' : '#64748B', width: 90 }}>
                Description:
              </Typography>
              <Typography variant="caption" sx={{ fontWeight: 600 }}>
                {study.studyDescription || 'Diagnostic Scan'}
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="caption" sx={{ color: isDark ? '#94A3B8' : '#64748B', width: 90 }}>
                Facility:
              </Typography>
              <Typography variant="caption" sx={{ fontWeight: 600 }}>
                {study.facility?.name || study.facilityName || 'General PACS'}
              </Typography>
            </Box>

            {study.studyDate && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="caption" sx={{ color: isDark ? '#94A3B8' : '#64748B', width: 90 }}>
                  Scan Date:
                </Typography>
                <Typography variant="caption" sx={{ fontWeight: 600 }}>
                  {new Date(study.studyDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                </Typography>
              </Box>
            )}

            {study.tatDeadline && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="caption" sx={{ color: isDark ? '#94A3B8' : '#64748B', width: 90 }}>
                  TAT Deadline:
                </Typography>
                <Typography variant="caption" sx={{ fontWeight: 700, color: study.tatStatus === 'critical' ? '#EF4444' : '#F59E0B' }}>
                  {new Date(study.tatDeadline).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} ({study.tatStatus || 'Active'})
                </Typography>
              </Box>
            )}
          </Stack>
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          py: 1.5,
          px: 2.5,
          borderTop: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'}`,
          justifyContent: 'space-between',
        }}
      >
        <Button
          size="small"
          onClick={onClose}
          disabled={claiming}
          sx={{ textTransform: 'none', color: isDark ? '#94A3B8' : '#64748B', fontSize: '0.75rem' }}
        >
          Cancel
        </Button>

        <Stack direction="row" spacing={1}>
          {onViewOnly && (
            <Button
              size="small"
              variant="outlined"
              startIcon={<ViewIcon sx={{ fontSize: 15 }} />}
              onClick={handleView}
              disabled={claiming}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '0.75rem',
                color: isDark ? '#CBD5E1' : '#475569',
                borderColor: isDark ? 'rgba(255,255,255,0.2)' : '#CBD5E1',
              }}
            >
              {isClaimedByOther ? 'View Images (Read-Only)' : 'View Only (No Lock)'}
            </Button>
          )}

          {isClaimedByMe && onUnclaim && (
            <Button
              size="small"
              variant="outlined"
              color="error"
              disabled={claiming || unclaiming}
              startIcon={unclaiming ? <CircularProgress size={14} color="inherit" /> : <LockOpenIcon sx={{ fontSize: 14 }} />}
              onClick={handleUnclaim}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '0.75rem',
                borderColor: 'rgba(239, 68, 68, 0.4)',
                '&:hover': { borderColor: '#EF4444', bgcolor: 'rgba(239, 68, 68, 0.08)' }
              }}
            >
              {unclaiming ? 'Releasing...' : 'Release / Unclaim'}
            </Button>
          )}

          {!isClaimedByOther && (
            <Button
              size="small"
              variant="contained"
              disabled={claiming}
              startIcon={claiming ? <CircularProgress size={14} color="inherit" /> : <LockIcon sx={{ fontSize: 14 }} />}
              onClick={handleClaim}
              sx={{
                bgcolor: '#4F46E5',
                '&:hover': { bgcolor: '#4338CA' },
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '0.75rem',
                px: 1.75,
              }}
            >
              {claiming ? 'Claiming...' : isClaimedByMe ? 'Continue Report' : 'Claim & Lock Study'}
            </Button>
          )}
        </Stack>
      </DialogActions>
    </Dialog>
  );
}
